import { usePrefetch } from '@openx/components-segment/lib/hooks/usePrefetch';
import { useSegmentContext } from '@openx/components-segment/lib/utils/context/SegmentContext';
import type { LalSegmentsListFilters } from '@openx/components-segment/types';
import { serverSideCriteriaProcessing } from '@openx/components-table/serverSideCriteriaProcessing';
import { CriteriaDimension, SortDirection } from '@openx/types/tableCriteria';
import { applyCriteriaChange } from '@openx/utils/lib/tableCriteria';
import isEqual from 'lodash/isEqual';
import { useCallback, useMemo } from 'react';
import { TABLE_PAGE_SIZE } from '../../../../config';
import { useHandleListCriteriaOnCompleted } from '../../../../hooks/useHandleListCriteriaOnCompleted';
import { type DefaultTableCriteria, useListCriteria } from '../../../../utils/useListCriteria';
import { adjustCriteriaFilters } from '../utils/adjustCriteriaFilters';

const defaultCriteria: DefaultTableCriteria<'lalSegments'> = {
  [CriteriaDimension.FILTERS]: {
    account: '',
    phrase: '',
    provider: '',
    status: '',
  },
  [CriteriaDimension.PAGINATION]: {
    pageNumber: 1,
    pageSize: TABLE_PAGE_SIZE,
    totalCount: 100,
  },
  [CriteriaDimension.SORT]: {
    column: 'created_date',
    direction: SortDirection.DESC,
    sortableColumns: [
      'lookalike_name',
      'status',
      'derived_balanced_reach',
      'auc_score',
      'created_date',
      'updated_date',
    ],
  },
};

export const useLalSegmentsList = () => {
  const { useLalSegmentsListFetch } = useSegmentContext();

  const { criteria, setCriteria } = useListCriteria('lalSegments', defaultCriteria);
  const handleCompleted = useHandleListCriteriaOnCompleted<LalSegmentsListFilters>(setCriteria);
  const queryFilters = useMemo(() => adjustCriteriaFilters(criteria), [criteria]);
  const queryCrit = useMemo(
    () => serverSideCriteriaProcessing({ ...criteria, filters: queryFilters }, [defaultCriteria.sort]),
    [criteria, queryFilters]
  );

  const { lalSegments, error, loading } = useLalSegmentsListFetch({
    options: {
      onCompleted: handleCompleted,
    },
    usePrefetch: fetchMore =>
      // biome-ignore lint/correctness/useHookAtTopLevel: need to be fixed in the future
      usePrefetch({
        fetchMore,
        pagination: criteria.pagination,
      }),
    variables: queryCrit,
  });

  const handleCriteriaChange = useCallback(
    newCriteria => {
      if (
        newCriteria?.dimension === 'sort' &&
        newCriteria.value?.column === criteria.sort?.column &&
        newCriteria.value?.direction === 'asc' &&
        newCriteria.value?.column !== defaultCriteria.sort?.column
      ) {
        newCriteria.value.column = defaultCriteria.sort?.column;
        newCriteria.value.direction = defaultCriteria.sort?.direction;
      }

      const newCriteriaProcessed = applyCriteriaChange(criteria, newCriteria);

      if (!isEqual({ ...newCriteriaProcessed }, { ...criteria })) {
        setCriteria(prev => ({ ...prev, ...newCriteriaProcessed }));
      }
    },
    [criteria, setCriteria]
  );

  return { lalSegments, error, loading, criteria, handleCriteriaChange };
};
